import { __assign } from 'tslib';
import React from 'react';
import { Check, X } from 'lucide-react';
import { color } from '../../atom/util/colors.js';
import '../../atom/util/dayjs.js';
import 'react-dom/client';
import useHAF from '../../atom/hook/useHAF.js';
import clsx from 'clsx';
import { cva } from 'class-variance-authority';
import '../../tailwind.css.js';

var divWrapperStyle = cva(['flex items-center'], {
    variants: {
        small: {
            true: ''
        },
        label: {
            true: 'gap-2',
        },
        labelPosition: {
            before: 'flex-row-reverse',
            after: ''
        }
    },
    compoundVariants: [
        {
            small: true,
            label: false,
            class: 'w-8 h-6'
        },
        {
            small: false,
            label: false,
            class: 'w-10 h-7'
        }
    ],
    defaultVariants: {
        small: false,
        label: false,
        labelPosition: 'after'
    }
});
var labelStyle = cva(['align-middle select-none whitespace-nowrap truncate'], {
    variants: {
        small: {
            true: 'text-sm leading-[18px] max-h-4',
            false: 'leading-5 max-h-5',
        },
        disabled: {
            true: 'text-disabled-text',
            false: 'text-dark-text',
        }
    },
    defaultVariants: {
        small: false,
        disabled: false
    }
});
var toggleButtonDivWrapperStyle = cva(['flex items-center overflow-visible rounded-lg h-2'], {
    variants: {
        small: {
            true: 'w-8',
            false: 'w-10'
        },
        disabled: {
            true: 'bg-gray-205',
            false: 'bg-gray-204'
        }
    },
    defaultVariants: {
        small: false,
        disabled: false
    }
});
var toggleButtonStyle = cva(['flex items-center justify-center rounded-full transition-transform ease-in-out duration-300'], {
    variants: {
        small: {
            true: 'w-4 h-4',
            false: 'w-5 h-5'
        },
        checked: {
            true: 'translate-x-full',
            false: 'translate-x-0 bg-white border-2'
        },
        disabled: {
            false: 'cursor-pointer'
        },
    },
    compoundVariants: [
        {
            disabled: true,
            checked: true,
            class: 'bg-disabled',
        },
        {
            disabled: true,
            checked: false,
            class: 'border-disabled',
        },
        {
            disabled: false,
            checked: true,
            class: 'hover:bg-brand-medium focus:bg-blue-301 bg-brand',
        },
        {
            disabled: false,
            checked: false,
            class: 'hover:border-brand-medium focus:border-blue-301 border-gray-203',
        },
        {
            disabled: false,
            class: 'hover:outline hover:outline-[5px] hover:outline-blue-306 focus:outline focus:outline-[5px] focus:outline-blue-305',
        },
    ],
    defaultVariants: {
        small: false,
        checked: false,
        disabled: false
    }
});
var Toggle = function (props) {
    var _a = useHAF(), ref = _a.ref, hover = _a.hover, focus = _a.focus;
    var fill = function () {
        if (props.checked) {
            return color('white');
        }
        else if (props.disabled) {
            return color('disabled-text');
        }
        else if (hover) {
            return color('brand-medium');
        }
        else if (focus) {
            return color('blue-301');
        }
        return color('gray-203');
    };
    var icon = function () {
        var dimensions = props.small ? { width: 9.5, height: 9.5 } : { width: 12, height: 12 };
        var Icon = props.checked ? Check : X;
        return React.createElement(Icon, __assign({}, dimensions, { color: fill(), strokeWidth: 4 }));
    };
    var onToggleHandler = function (event) {
        var _a;
        event.preventDefault();
        (_a = props.onChange) === null || _a === void 0 ? void 0 : _a.call(props, !props.checked);
    };
    return (React.createElement("div", { className: props.className },
        React.createElement("label", { className: clsx('flex flex-col w-fit', { 'cursor-pointer': !props.disabled }) },
            React.createElement("div", { className: divWrapperStyle({
                    small: props.small,
                    label: !!props.label,
                    labelPosition: props.labelPosition
                }) },
                React.createElement("div", { className: toggleButtonDivWrapperStyle({
                        small: !!props.small,
                        disabled: !!props.disabled
                    }) },
                    React.createElement("button", { "data-testid": "toggle-button", ref: ref, disabled: props.disabled, onClick: onToggleHandler, className: toggleButtonStyle({
                            small: !!props.small,
                            checked: !!props.checked,
                            disabled: !!props.disabled,
                        }) }, icon())),
                props.label && (React.createElement("div", { className: labelStyle({ small: !!props.small, disabled: !!props.disabled }) }, props.label))))));
};

export { Toggle as default };
